import { useEffect } from "react";

function ensureMeta(selector, identity, content) {
  let node = document.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(identity).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

export default function LegalPage({
  title,
  description,
  canonical,
  updatedLabel,
  intro,
  sections,
}) {
  useEffect(() => {
    document.title = `${title} | BlackRide`;
    ensureMeta('meta[name="description"]', { name: "description" }, description);
    ensureMeta('meta[property="og:title"]', { property: "og:title" }, `${title} | BlackRide`);
    ensureMeta('meta[property="og:description"]', { property: "og:description" }, description);
    ensureMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }, `${title} | BlackRide`);
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) canonicalTag.setAttribute("href", canonical);

    const oldSchema = document.getElementById("legal-page-schema");
    if (oldSchema) oldSchema.remove();

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "legal-page-schema";
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: canonical,
      publisher: {
        "@type": "Organization",
        name: "BlackRide",
        url: "https://getblackride.com",
      },
    });
    document.head.appendChild(schema);

    return () => schema.remove();
  }, [canonical, description, title]);

  return (
    <div className="min-h-screen bg-black text-white pt-24 md:pt-28 legal-page-shell">
      <section className="px-6 md:px-12 py-14 md:py-18 border-b border-white/8">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] mb-4">Legal</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-5">{title}</h1>
          <p className="text-base md:text-lg text-gray-300 leading-8 max-w-3xl">{intro}</p>
          <p className="mt-5 text-sm text-gray-500">{updatedLabel}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 py-14 md:py-16">
        <div className="max-w-5xl mx-auto grid gap-6">
          {sections.map((section) => (
            <article key={section.title} className="rounded-[28px] border border-white/8 bg-[#111] p-6 md:p-8 shadow-[0_24px_60px_rgba(0,0,0,0.22)]">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white mb-4">{section.title}</h2>
              <div className="space-y-4 text-gray-300 leading-8 text-[15px] md:text-base">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.bullets?.length ? (
                  <ul className="space-y-3 pl-5 list-disc marker:text-[#C7A239]">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </article>
          ))}

          <div className="rounded-[28px] border border-[#C7A239]/20 bg-[#121212] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] mb-3">Contact</p>
            <p className="text-gray-300 leading-8 text-[15px] md:text-base">
              For privacy, legal, or booking policy questions, contact BlackRide on WhatsApp at <a className="legal-page-contact-link text-white underline underline-offset-4" href="https://wa.me/966540423401" target="_blank" rel="noreferrer">+966 54 042 3401</a> or email <a className="text-white underline underline-offset-4" href="mailto:hello@getblackride.com">hello@getblackride.com</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
